import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Calendar, Clock, MapPin, Users, Plus, Search, Filter, Video, Building2,
  ChevronRight, ExternalLink, Star, Sparkles, ArrowRight, X, Check,
  Heart, MessageCircle, Send, UserCircle, Megaphone, Pencil, Trash2,
  Image as ImageIcon, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { ActivateModal } from '../components/PremiumGate';

// Helper function to extract error message from API responses
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d).join(', ');
  }
  return typeof detail === 'string' ? detail : fallback;
};

const EVENT_TYPES = [
  { id: 'all', label: 'All Events' },
  { id: 'virtual', label: 'Virtual', icon: Video },
  { id: 'in_person', label: 'In-Person', icon: Building2 },
];

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatTime = (timeStr) => {
  if (!timeStr) return 'TBD';
  return timeStr;
};

// Event Card Component
const EventCard = ({ event, onRSVP, onDelete, onEdit, currentUserId, onViewDetail }) => {
  const isRegistered = event.user_rsvp;
  const attendeeCount = event.rsvp_count || 0;
  const isCreator = event.creator_id === currentUserId;
  
  return (
    <Card className={`overflow-hidden hover:shadow-lg transition-all group cursor-pointer ${event.is_featured ? 'border-amber-300 ring-1 ring-amber-200' : ''}`} data-testid={`event-${event.id}`} onClick={() => onViewDetail(event)}>
      {event.image_url ? (
        <div className="h-40 overflow-hidden relative">
          <img src={resolveImageUrl(event.image_url)} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          <div className="absolute top-3 left-3 flex gap-1.5">
            <Badge className={event.event_type === 'virtual' ? 'bg-blue-500' : 'bg-green-500'}>
              {event.event_type === 'virtual' ? <Video className="w-3 h-3 mr-1" /> : <Building2 className="w-3 h-3 mr-1" />}
              {event.event_type === 'virtual' ? 'Virtual' : 'In-Person'}
            </Badge>
            {event.is_featured && <Badge className="bg-amber-500"><Star className="w-3 h-3 mr-1" />Featured</Badge>}
          </div>
        </div>
      ) : (
        <div className="h-24 bg-gradient-to-br from-primary/20 to-purple-500/20 relative flex items-center justify-center">
          <Calendar className="w-10 h-10 text-primary/30" />
          <div className="absolute top-3 left-3 flex gap-1.5">
            <Badge className={event.event_type === 'virtual' ? 'bg-blue-500' : 'bg-green-500'}>
              {event.event_type === 'virtual' ? 'Virtual' : 'In-Person'}
            </Badge>
            {event.is_featured && <Badge className="bg-amber-500"><Star className="w-3 h-3 mr-1" />Featured</Badge>}
          </div>
        </div>
      )}
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3 text-primary">
          <Calendar className="w-4 h-4" />
          <span className="text-sm font-medium">{formatDate(event.event_date)}</span>
          <span className="text-muted-foreground">·</span>
          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{formatTime(event.event_time)} {event.timezone}</span>
        </div>
        <h3 className="font-semibold text-lg mb-2 line-clamp-2">{event.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{event.description}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <MapPin className="w-3.5 h-3.5" />
          <span>{event.location || (event.event_type === 'virtual' ? 'Online Event' : 'Location TBD')}</span>
        </div>
        {event.event_type === 'virtual' && event.link && (
          <a href={event.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:underline mb-4 truncate"
            data-testid={`meeting-link-${event.id}`}
          >
            <Video className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{event.link.includes('meet.jit.si') ? 'Join Jitsi Meeting' : 'Join Virtual Meeting'}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        )}
        {!(event.event_type === 'virtual' && event.link) && <div className="mb-2" />}
        <div className="flex items-center gap-3 pb-4 border-b border-border/50" onClick={e => e.stopPropagation()}>
          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
            {event.creator_photo ? (
              <img src={resolveImageUrl(event.creator_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs font-bold text-primary">{(event.creator_name || 'H')[0]}</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium">Hosted by {event.creator_name}</p>
            <p className="text-[10px] text-muted-foreground">{event.creator_profession}</p>
          </div>
          {isCreator && (
            <div className="flex gap-1">
              <button onClick={() => onEdit(event)} className="p-1.5 rounded hover:bg-muted transition-colors" data-testid={`edit-event-${event.id}`}>
                <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              <button onClick={() => onDelete(event.id)} className="p-1.5 rounded hover:bg-red-50 transition-colors" data-testid={`delete-event-${event.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            </div>
          )}
        </div>
        <div className="pt-4" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Users className="w-3.5 h-3.5" />
              <span>{attendeeCount} {attendeeCount === 1 ? 'attendee' : 'attendees'}</span>
            </div>
            {event.max_attendees && (
              <span className="text-[10px] text-muted-foreground">{event.max_attendees - attendeeCount} spots left</span>
            )}
          </div>
          {event.max_attendees && (
            <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-3">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min(100, (attendeeCount / event.max_attendees) * 100)}%` }} />
            </div>
          )}
          <Button size="sm" variant={isRegistered ? 'outline' : 'default'} onClick={() => onRSVP(event.id)}
            className={`w-full ${isRegistered ? 'text-green-600 border-green-600' : ''}`} data-testid={`rsvp-${event.id}`}>
            {isRegistered ? (<><Check className="w-3.5 h-3.5 mr-1" />Registered</>) : (<><UserCircle className="w-3.5 h-3.5 mr-1" />RSVP Now</>)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Create Event Modal
const CreateEventModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    timezone: 'EST',
    event_type: 'virtual',
    location: '',
    link: '',
    max_attendees: '',
    promote_to_feed: true,
    image_url: ''
  });
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('connectmail_token')}` },
        body: formData
      });
      const data = await resp.json();
      if (data.url) {
        setForm(f => ({ ...f, image_url: data.url }));
        toast.success('Image uploaded!');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.event_date) {
      toast.error('Please fill in required fields');
      return;
    }
    setCreating(true);
    try {
      const cleanedForm = { ...form };
      // Convert empty strings to null for optional numeric fields
      if (cleanedForm.max_attendees === '' || cleanedForm.max_attendees === undefined) {
        cleanedForm.max_attendees = null;
      } else {
        cleanedForm.max_attendees = parseInt(cleanedForm.max_attendees, 10) || null;
      }
      await onCreate(cleanedForm);
      toast.success('Event created successfully!');
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create event'));
    } finally {
      setCreating(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="create-event-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Create Event</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Cover Image Upload */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Event Image</label>
            {form.image_url ? (
              <div className="relative rounded-lg overflow-hidden h-32 bg-gray-100">
                <img src={resolveImageUrl(form.image_url)} alt="Event" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setForm({...form, image_url: ''})} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors" data-testid="event-image-upload">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                {uploading ? (
                  <span className="text-xs text-muted-foreground">Uploading...</span>
                ) : (
                  <>
                    <ImageIcon className="w-5 h-5 text-muted-foreground mb-1" />
                    <span className="text-xs text-muted-foreground">Upload cover image</span>
                  </>
                )}
              </label>
            )}
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Event Title *</label>
            <Input
              value={form.title}
              onChange={e => setForm({...form, title: e.target.value})}
              placeholder="e.g., Local Business Networking Mixer"
              data-testid="event-title-input"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1.5 block">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              placeholder="Describe your event..."
              className="w-full h-24 px-3 py-2 text-sm border border-border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="event-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Date *</label>
              <Input
                type="date"
                value={form.event_date}
                onChange={e => setForm({...form, event_date: e.target.value})}
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Time</label>
              <Input
                type="time"
                value={form.event_time}
                onChange={e => setForm({...form, event_time: e.target.value})}
                data-testid="event-time-input"
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1.5 block">Event Type</label>
            <div className="flex gap-3">
              {['virtual', 'in_person'].map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setForm({...form, event_type: type})}
                  className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-all ${
                    form.event_type === type
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`event-type-${type}`}
                >
                  {type === 'virtual' ? (
                    <><Video className="w-4 h-4 inline mr-2" /> Virtual</>
                  ) : (
                    <><Building2 className="w-4 h-4 inline mr-2" /> In-Person</>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {form.event_type === 'in_person' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">Location</label>
              <Input
                value={form.location}
                onChange={e => setForm({...form, location: e.target.value})}
                placeholder="e.g., 123 Main St, Troy, MI"
                data-testid="event-location-input"
              />
            </div>
          )}

          {form.event_type === 'virtual' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
                <Video className="w-3.5 h-3.5 text-blue-500" />
                Virtual Meeting Link
              </label>
              <Input
                value={form.link}
                onChange={e => setForm({...form, link: e.target.value})}
                placeholder="Paste your own Zoom/Teams link, or leave blank for auto-generated Jitsi Meet"
                data-testid="event-virtual-link-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1">
                <Link2 className="w-3 h-3" />
                {form.link ? 'Using your custom link' : 'A free Jitsi Meet link will be auto-generated'}
              </p>
            </div>
          )}

          {/* Promote to Feed Toggle */}
          <div className="bg-gradient-to-r from-primary/5 to-purple-500/5 border border-primary/20 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="promote-to-feed-toggle">
              <div className="flex-shrink-0 mt-0.5">
                <button
                  type="button"
                  onClick={() => setForm({...form, promote_to_feed: !form.promote_to_feed})}
                  className={`w-10 h-6 rounded-full transition-colors ${form.promote_to_feed ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${form.promote_to_feed ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
              <div className="flex-1" onClick={() => setForm({...form, promote_to_feed: !form.promote_to_feed})}>
                <div className="flex items-center gap-1.5">
                  <Megaphone className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold">Promote in Live Feed</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Your event will appear as a featured post in the community feed, reaching all members instantly.
                </p>
              </div>
            </label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={creating} className="flex-1" data-testid="create-event-submit">
              {creating ? 'Creating...' : 'Create Event'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Introduce Yourself Modal
const IntroduceModal = ({ onClose, onPost, user }) => {
  const [bio, setBio] = useState('');
  const [posting, setPosting] = useState(false);
  const userCity = user?.city || '';
  const userState = user?.state || '';
  const location = [userCity, userState].filter(Boolean).join(', ') || 'your area';

  const handlePost = async () => {
    if (!bio.trim()) {
      toast.error('Please write a brief introduction');
      return;
    }
    setPosting(true);
    try {
      await onPost(bio, userCity, userState);
      toast.success('Your introduction has been posted to the community!');
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to post introduction'));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background rounded-xl shadow-2xl max-w-lg w-full p-6"
        onClick={e => e.stopPropagation()}
        data-testid="introduce-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Introduce Yourself</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 bg-primary/5 rounded-lg px-3 py-2">
          <MapPin className="w-4 h-4 text-primary" />
          <span>Posting to <strong className="text-foreground">{location}</strong> community</span>
        </div>

        <div className="flex gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {user?.profile_photo_url ? (
              <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover rounded-full" />
            ) : (
              <span className="text-primary font-bold text-sm">{(user?.full_name || 'U')[0]}</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{user?.full_name}</p>
            <p className="text-xs text-muted-foreground">{user?.company}</p>
          </div>
        </div>

        <textarea
          value={bio}
          onChange={e => setBio(e.target.value)}
          placeholder={`Hi ${location}! I'm a ${user?.company ? user.company + ' professional' : 'local business owner'} and would love to connect with nearby professionals. Tell everyone what you do and what you're looking for...`}
          className="w-full h-32 px-3 py-2 text-sm border border-border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 mb-4"
          data-testid="intro-bio-input"
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground text-right mb-4">{bio.length}/500</p>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handlePost} disabled={posting || !bio.trim()} className="flex-1" data-testid="post-intro-btn">
            {posting ? 'Posting...' : (
              <><Send className="w-4 h-4 mr-2" />Post Introduction</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};


// Edit Event Modal
const EditEventModal = ({ event, onClose, onSave }) => {
  const [form, setForm] = useState({
    title: event.title || '',
    description: event.description || '',
    event_date: event.event_date || '',
    event_time: event.event_time || '',
    timezone: event.timezone || 'EST',
    event_type: event.event_type || 'virtual',
    location: event.location || '',
    link: event.link || '',
    max_attendees: event.max_attendees || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.event_date) {
      toast.error('Please fill in required fields');
      return;
    }
    setSaving(true);
    try {
      const cleanedForm = { ...form };
      if (cleanedForm.max_attendees === '' || cleanedForm.max_attendees === undefined) {
        cleanedForm.max_attendees = null;
      } else {
        cleanedForm.max_attendees = parseInt(cleanedForm.max_attendees, 10) || null;
      }
      await onSave(cleanedForm);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="edit-event-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Edit Event</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Event Title *</label>
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="edit-event-title" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              className="w-full h-24 px-3 py-2 text-sm border border-border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="edit-event-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Date *</label>
              <Input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} data-testid="edit-event-date" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Time</label>
              <Input type="time" value={form.event_time} onChange={e => setForm({...form, event_time: e.target.value})} data-testid="edit-event-time" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Event Type</label>
            <div className="flex gap-3">
              {['virtual', 'in_person'].map(type => (
                <button key={type} type="button" onClick={() => setForm({...form, event_type: type})}
                  className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-all ${form.event_type === type ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}>
                  {type === 'virtual' ? <><Video className="w-4 h-4 inline mr-2" /> Virtual</> : <><Building2 className="w-4 h-4 inline mr-2" /> In-Person</>}
                </button>
              ))}
            </div>
          </div>
          {form.event_type === 'in_person' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">Location</label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="edit-event-location" />
            </div>
          )}
          {form.event_type === 'virtual' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
                <Video className="w-3.5 h-3.5 text-blue-500" />
                Virtual Meeting Link
              </label>
              <Input
                value={form.link}
                onChange={e => setForm({...form, link: e.target.value})}
                placeholder="Paste your own link, or leave blank for Jitsi Meet"
                data-testid="edit-event-virtual-link"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1">
                <Link2 className="w-3 h-3" />
                {form.link ? 'Using custom link' : 'Current Jitsi Meet link will be kept'}
              </p>
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1" data-testid="save-event-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Community Intro Card
const CommunityIntroCard = ({ intro, onLike, currentUserId }) => (
  <Card className="hover:shadow-md transition-all" data-testid={`intro-${intro.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {intro.user_photo ? (
            <img src={resolveImageUrl(intro.user_photo)} alt="" className="w-full h-full object-cover rounded-full" />
          ) : (
            <span className="text-primary font-bold text-sm">{(intro.user_name || 'U')[0]}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm">{intro.user_name}</p>
            <span className="text-xs text-muted-foreground">•</span>
            <p className="text-xs text-muted-foreground">{intro.user_profession}</p>
          </div>
          <div className="flex items-center gap-1 text-xs text-primary mb-2">
            <MapPin className="w-3 h-3" />
            <span>{intro.location}</span>
          </div>
          <p className="text-sm text-foreground/80 line-clamp-3">{intro.content}</p>
          <div className="flex items-center gap-4 mt-3">
            <button
              onClick={() => onLike(intro.id)}
              className={`flex items-center gap-1.5 text-xs transition-colors ${intro.liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}
              data-testid={`like-intro-${intro.id}`}
            >
              <Heart className={`w-3.5 h-3.5 ${intro.liked ? 'fill-current' : ''}`} />
              <span>{intro.like_count || 0}</span>
            </button>
            <span className="text-xs text-muted-foreground">
              {new Date(intro.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const Events = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [intros, setIntros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const isPremium = user?.is_premium === true;
  const [showIntroduce, setShowIntroduce] = useState(false);
  const [activeTab, setActiveTab] = useState('events'); // 'events' | 'introductions'
  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingEventId, setDeletingEventId] = useState(null);
  
  const fetchEvents = useCallback(async () => {
    try {
      const [eventsData, introsData] = await Promise.all([
        api.getEvents(),
        api.getCommunityIntros(user?.city)
      ]);
      setEvents(eventsData.events || []);
      setIntros(introsData.introductions || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.city]);
  
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  
  const handleRSVP = async (eventId) => {
    try {
      const result = await api.rsvpEvent(eventId);
      setEvents(events.map(e => {
        if (e.id === eventId) {
          return {
            ...e,
            user_rsvp: result.rsvp,
            rsvp_count: result.rsvp ? (e.rsvp_count || 0) + 1 : Math.max(0, (e.rsvp_count || 0) - 1)
          };
        }
        return e;
      }));
      toast.success(result.rsvp ? 'RSVP confirmed!' : 'RSVP cancelled');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to RSVP'));
    }
  };
  
  const handleCreateEvent = async (eventData) => {
    const newEvent = await api.createEvent(eventData);
    setEvents([newEvent, ...events]);
  };

  const handlePostIntro = async (bio, city, state) => {
    const result = await api.postCommunityIntro(bio, city, state);
    setIntros([result, ...intros]);
  };

  const handleLikeIntro = async (introId) => {
    try {
      const result = await api.likeCommunityIntro(introId);
      setIntros(intros.map(i => {
        if (i.id === introId) {
          return { ...i, liked: result.liked, like_count: result.liked ? (i.like_count || 0) + 1 : Math.max(0, (i.like_count || 0) - 1) };
        }
        return i;
      }));
    } catch (err) {
      toast.error('Failed to like');
    }
  };

  const handleEditEvent = async (eventData) => {
    try {
      const updated = await api.updateEvent(editingEvent.id, eventData);
      setEvents(events.map(e => e.id === editingEvent.id ? { ...e, ...updated } : e));
      toast.success('Event updated!');
      setEditingEvent(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update event'));
    }
  };

  const handleDeleteEvent = async () => {
    try {
      await api.deleteEvent(deletingEventId);
      setEvents(events.filter(e => e.id !== deletingEventId));
      toast.success('Event deleted');
      setDeletingEventId(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete event'));
    }
  };

  const handleViewEventDetail = (event) => {
    navigate(`/events/${event.id}`);
  };
  
  // Filter events
  const filteredEvents = events.filter(e => {
    if (filter !== 'all' && e.event_type !== filter) return false;
    if (search && !e.title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  
  // Separate upcoming and past events
  const now = new Date();
  const upcomingEvents = filteredEvents.filter(e => new Date(e.event_date) >= now);
  const pastEvents = filteredEvents.filter(e => new Date(e.event_date) < now);
  
  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded-lg w-1/3" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-72 bg-muted rounded-xl" />)}
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="space-y-6" data-testid="events-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">Networking Events</h1>
            <p className="text-muted-foreground text-sm">Connect with professionals at virtual and in-person events</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowIntroduce(true)} data-testid="introduce-yourself-btn">
              <UserCircle className="w-4 h-4 mr-2" />
              Introduce Yourself
            </Button>
            <Button onClick={() => setShowCreate(true)} data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Host an Event
            </Button>
          </div>
        </div>

        {/* Community Introduce Yourself Banner */}
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-blue-500/5" data-testid="intro-banner">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm">
                    Introduce yourself to the {user?.city ? `${user.city}` : 'local'} business community
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Let nearby professionals know who you are and what you do
                  </p>
                </div>
              </div>
              <Button size="sm" onClick={() => setShowIntroduce(true)} data-testid="intro-banner-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Post Introduction
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tab Toggle: Events / Community Introductions */}
        <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'events' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('events')}
            data-testid="tab-events"
          >
            <Calendar className="w-4 h-4 inline mr-1.5" />
            Events
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'introductions' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('introductions')}
            data-testid="tab-introductions"
          >
            <Users className="w-4 h-4 inline mr-1.5" />
            Community Intros
            {intros.length > 0 && <span className="ml-1.5 bg-primary text-primary-foreground text-[10px] rounded-full px-1.5 py-0.5">{intros.length}</span>}
          </button>
        </div>

        {activeTab === 'introductions' ? (
          /* Community Introductions Tab */
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {user?.city ? `${user.city} Community` : 'Community Introductions'}
              </h2>
              <Button size="sm" onClick={() => setShowIntroduce(true)}>
                <Plus className="w-4 h-4 mr-1.5" />
                Introduce Yourself
              </Button>
            </div>
            {intros.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {intros.map(intro => (
                  <CommunityIntroCard
                    key={intro.id}
                    intro={intro}
                    onLike={handleLikeIntro}
                    currentUserId={user?.id}
                  />
                ))}
              </div>
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No introductions yet</h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    Be the first to introduce yourself to the {user?.city || 'local'} business community!
                  </p>
                  <Button onClick={() => setShowIntroduce(true)}>
                    <Send className="w-4 h-4 mr-2" />
                    Post Your Introduction
                  </Button>
                </CardContent>
              </Card>
            )}
          </section>
        ) : (
          /* Events Tab */
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search events..."
                  className="pl-10"
                  data-testid="event-search"
                />
              </div>
              <div className="flex gap-2">
                {EVENT_TYPES.map(type => (
                  <Button
                    key={type.id}
                    variant={filter === type.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter(type.id)}
                    data-testid={`filter-${type.id}`}
                  >
                    {type.icon && <type.icon className="w-3.5 h-3.5 mr-1.5" />}
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Upcoming Events */}
            {upcomingEvents.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Upcoming Events ({upcomingEvents.length})
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {upcomingEvents.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onRSVP={handleRSVP}
                      onEdit={setEditingEvent}
                      onDelete={setDeletingEventId}
                      currentUserId={user?.id}
                      onViewDetail={handleViewEventDetail}
                    />
                  ))}
                </div>
              </section>
            )}
            
            {/* Empty State */}
            {upcomingEvents.length === 0 && (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No upcoming events</h3>
                  <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
                    Be the first to host a networking event in your area and connect with local professionals.
                  </p>
                  <Button onClick={() => setShowCreate(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Host an Event
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Past Events */}
            {pastEvents.length > 0 && (
              <section className="pt-6 border-t border-border/50">
                <h2 className="text-lg font-semibold mb-4 text-muted-foreground">Past Events</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-60">
                  {pastEvents.slice(0, 3).map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onRSVP={handleRSVP}
                      onEdit={setEditingEvent}
                      onDelete={setDeletingEventId}
                      currentUserId={user?.id}
                      onViewDetail={handleViewEventDetail}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
        
        {/* Create Event Modal */}
        {showCreate && (
          <CreateEventModal
            onClose={() => setShowCreate(false)}
            onCreate={handleCreateEvent}
          />
        )}

        {/* Introduce Yourself Modal */}
        {showIntroduce && (
          <IntroduceModal
            onClose={() => setShowIntroduce(false)}
            onPost={handlePostIntro}
            user={user}
          />
        )}

        {/* Edit Event Modal */}
        {editingEvent && (
          <EditEventModal
            event={editingEvent}
            onClose={() => setEditingEvent(null)}
            onSave={handleEditEvent}
          />
        )}

        {/* Delete Confirmation Modal */}
        {deletingEventId && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDeletingEventId(null)}>
            <div className="bg-background rounded-xl shadow-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()} data-testid="delete-event-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Delete Event</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to delete this event? All RSVPs and associated feed posts will also be removed.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setDeletingEventId(null)} className="flex-1" data-testid="cancel-delete-btn">Cancel</Button>
                <Button variant="destructive" onClick={handleDeleteEvent} className="flex-1" data-testid="confirm-delete-btn">Delete Event</Button>
              </div>
            </div>
          </div>
        )}
        {showActivate && null}
      </div>
    </Layout>
  );
};

export default Events;
